/**
 * Turning a server-issued expiry into a deadline the client can count down to.
 *
 * Shared by the login MFA challenge and TOTP enrollment — used by authService
 * and mfaService respectively, at the boundary where each response arrives,
 * never by the dialogs that render the countdowns. Both are handed an
 * `expires_at` read from the server's clock and both need to count down against
 * the browser's, and those two clocks disagree in the field more often than is
 * comfortable. Resolving in the service is what keeps the correction next to
 * the `Date` header it needs, and keeps a raw server timestamp from reaching a
 * component that could compare it to `Date.now()`.
 *
 * Two steps, in this order, and the order matters:
 *
 *   1. `toClientClockSeconds` *measures* the disagreement, using the `Date`
 *      header from the same response, and cancels it out.
 *   2. `resolveExpiryDeadline` turns what is left into a relative window and
 *      anchors it to the client's own clock, rejecting anything still
 *      implausible.
 *
 * Step 2 alone is not enough, which is what step 1 was added for. It rejects
 * gross skew but silently accepts moderate skew: a client six minutes slow
 * given a five-minute window derives an eleven-minute one, under the
 * plausibility cap, and counts down from it. See ENROLLMENT_FALLBACK_WINDOW_MS
 * for why running long is the expensive direction to be wrong in.
 */

// A window further out than this is not a long session, it is a skewed client
// clock — nothing the API issues comes close. Deliberately generous: this only
// needs to catch gross skew, not to tune precision.
export const MAX_PLAUSIBLE_WINDOW_MS = 15 * 60 * 1000;

/**
 * Fallback window for TOTP enrollment, used only when `expires_at` is missing
 * or implausible.
 *
 * Deliberately shorter than the five minutes both known server paths use
 * (`identity/local/totp.go:36`'s enrollmentTTL, and the five-minute fallback at
 * `identity/gcip/enrollment.go:39` when the provider's FinalizeEnrollmentTime
 * will not parse). Five minutes is not a contract: in production the real
 * deadline is whatever Identity Platform puts in FinalizeEnrollmentTime, which
 * we do not control and cannot see from here.
 *
 * The haircut is because the two ways of being wrong are not equally bad. Run
 * long and, at the moment the server has already expired the session, the UI
 * still shows time remaining — so the 403 INVALID_CODE that comes back gets
 * rendered as "that code is incorrect" when the real cause was expiry. That is
 * precisely the misattribution the countdown exists to prevent, and the server
 * cannot distinguish the two cases for us. Run short and the UI says "expired,
 * start over" while the session was in fact still good: annoying, but honest,
 * and one click to recover.
 */
export const ENROLLMENT_FALLBACK_WINDOW_MS = 4 * 60 * 1000;

/**
 * Fallback window for the login MFA challenge, used only when `expires_at` is
 * missing or implausible.
 *
 * A full five minutes, and deliberately **not** given the haircut its
 * neighbour above gets. The difference is not an oversight and should not be
 * tidied into consistency: the two flows are wrong in different ways.
 *
 * Enrollment must err short because running long makes it say "that code is
 * incorrect" for what was really an expiry, and the server cannot tell the two
 * apart. The login challenge has no such failure — running long simply lands
 * on the 401, whose copy already covers both a wrong code and a dead
 * challenge, so the user is told something true either way. Paying for a
 * safety margin here would buy nothing and cost real /login round trips.
 *
 * Five is also a genuine number rather than an estimate: `mfaChallengeLifetime`
 * (`internal/authorization/models/mfa_challenge.go:11`) is five minutes and the
 * server enforces it itself, which is exactly the difference from enrollment's
 * unobservable provider-side deadline.
 */
export const CHALLENGE_FALLBACK_WINDOW_MS = 5 * 60 * 1000;

/**
 * Re-express a server-issued `expires_at` (Unix SECONDS) on the client's own
 * clock, using the `Date` header of the response that carried it.
 *
 * `Date` is the server's clock at the moment it sent the response, so
 * `Date - receivedAt` is the skew itself — measured, not inferred from whether
 * the result looks silly afterwards. Subtracting it leaves a timestamp on the
 * client's clock, which is the only clock every later comparison uses.
 *
 * Returns the value **unchanged** when the header is absent or unparseable —
 * some proxies strip it — so the plausibility cap below remains the only
 * defence in that case, exactly as it was before this existed. It is also
 * returned unchanged when it is not a usable timestamp at all; deciding what to
 * do about that is resolveExpiryDeadline's job, not this one's, and doing it
 * twice would mean two places to keep agreeing.
 *
 * The correction is deliberately not latency-compensated. `Date` is stamped
 * before the response travels, so `receivedAt` is later by roughly the
 * downstream leg, and the skew therefore comes out slightly *over*-stated and
 * the window slightly short. That is the safe direction — a countdown that
 * finishes early says "start over", one that finishes late says "wrong code"
 * about a session that had in fact expired.
 *
 * @param {number} expiresAtSeconds Unix timestamp in seconds, from the API.
 * @param {string|null|undefined} serverDateHeader The response's `Date` header.
 * @param {number} [receivedAt] Epoch milliseconds when the response arrived.
 * @returns {number} `expiresAtSeconds` on the client's clock, or as given.
 */
export const toClientClockSeconds = (
  expiresAtSeconds,
  serverDateHeader,
  receivedAt = Date.now(),
) => {
  if (typeof expiresAtSeconds !== 'number' || !(expiresAtSeconds > 0)) return expiresAtSeconds;
  if (typeof serverDateHeader !== 'string') return expiresAtSeconds;

  // Date.parse understands the RFC 7231 IMF-fixdate that `Date` is required to
  // be, and answers NaN for anything it does not — including a header a proxy
  // has rewritten into something else.
  const serverNowMs = Date.parse(serverDateHeader);
  if (Number.isNaN(serverNowMs)) return expiresAtSeconds;

  return expiresAtSeconds - (serverNowMs - receivedAt) / 1000;
};

/**
 * Resolve an `expires_at` (Unix SECONDS) into a deadline measured on the
 * client's own clock.
 *
 * The timestamp is consulted exactly once, to derive a *duration*. Every
 * comparison after that is client-clock against client-clock, so skew shifts
 * the whole window uniformly instead of collapsing it. Comparing the two clocks
 * directly is not a cosmetic problem: a browser running fast sees every
 * freshly-issued window as already dead and can never complete the flow at all.
 *
 * That one crossing is where residual skew gets in, and it is why callers hand
 * this a value that has already been through `toClientClockSeconds`: with the
 * skew measured and removed, the duration derived here is the real one. Without
 * it — no `Date` header — the cap below is all that stands between a slow
 * browser and a countdown longer than the session it is counting.
 *
 * A duration that is negative or implausibly long is evidence of skew rather
 * than of an unusual window, so it falls back to `fallbackMs` — which is
 * relative, and therefore cannot itself be skewed.
 *
 * `now` is captured once by the caller (or defaulted here) and used for both
 * the delta and the anchor, so `deadline = now + window` holds exactly. Reading
 * the clock twice would let a blocked thread — a slow render, a GC pause, a
 * debugger sitting on a breakpoint — slip between the two readings and anchor
 * the deadline later than the window actually measured.
 *
 * @param {number} expiresAtSeconds Unix timestamp in seconds, from the API.
 * @param {number} fallbackMs Relative window to use when the value is unusable.
 * @param {number} [now] Epoch milliseconds; injectable so tests need no fake timers.
 * @returns {number} Epoch milliseconds at which the window closes.
 */
export const resolveExpiryDeadline = (expiresAtSeconds, fallbackMs, now = Date.now()) => {
  // The typeof guard is load-bearing: readBody hands back untyped JSON, so a
  // string "1733960300" would otherwise coerce through `* 1000` and look valid.
  // NaN fails every comparison below, so a missing, zero, or non-numeric value
  // reaches the fallback without needing a branch of its own.
  const windowMs =
    typeof expiresAtSeconds === 'number' && expiresAtSeconds > 0
      ? expiresAtSeconds * 1000 - now
      : NaN;

  const usable = windowMs > 0 && windowMs <= MAX_PLAUSIBLE_WINDOW_MS ? windowMs : fallbackMs;
  return now + usable;
};
